import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigation } from "../../../shared/utils/useNavigation";
import {
    showError,
    showSignInRequired,
    showFollowRequired,
    showSubscriptionRequired,
    confirmUnfollow,
} from "../../../shared/utils/swalHandler";
import { ROLE, PURCHASE_STATE, OFFER_TYPE } from "../../../shared/constants/common";
import { unfollowPPV, followPPV } from "../../../shared/actions/followAction";
import { checkPromotion } from "../../../shared/actions/promotionAction";
import {
    getDiscountOfferText,
    getPromotionRemainingText,
    getSubscriptionPrice,
    getAmountWithExtraCharge,
    isFreeTrialSubscriptionFunc,
    getSubscriptionText,
    getEndSubscriptionText,
    getFormFieldValue
} from "../../../shared/utils/paymentUtil";
import { calculateDaysRemaining, formatDateTimeZone } from "../../../shared/utils/function";
import { reportError } from "../../../shared/sentry";

// Modals
// import PPVFollowModal from "../../../views/Modals/PPVFollowModal";
import SubscribeModalLoggedIn from "../../../views/Modals/SubscribeModal/SubscribeModalLoggedIn";
import SubscribeModalWaitingPayment from "../../../views/Modals/SubscribeModal/SubscribeModalWaitingPayment";
import UnsubscribeModal from "../../../views/Modals/UnsubscribeModal";
import UnsubscriptionModalInProgress from "../../../views/Modals/SubscribeModal/UnsubscriptionModalInProgress";

const ProfileActions = ({
    creatorInfo,
    subscribed,
    vatPercentage,
    subscriptionLoading,
    isEmailConfirmed,
    loggedIn,
    onSubscriptionSuccess,
}) => {
    const navigate = useNavigation();
    const creatorRole = creatorInfo?.rol;

    // State
    const [ppvFollowModalOpen, setPpvFollowModalOpen] = useState(false);
    const [subscribeModalOpenSignal, setSubscribeModalOpenSignal] = useState(0);
    const [followLoading, setFollowLoading] = useState(false);
    const [promotionData, setPromotionData] = useState(null);

    const price = creatorInfo?.user_subsData?.price;
    // Calculate discounted price based on promotion data
    const getPrice = getSubscriptionPrice(promotionData, creatorInfo?.user_subsData?.real_price, price);

    useEffect(() => {
        if (ppvFollowModalOpen) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }

        // Cleanup: remove class on unmount
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [ppvFollowModalOpen]);

    // Message button handler
    const messageBtn = () => {
        if (loggedIn && subscribed) {
            // Open the chat with this profile's user
            if (creatorInfo?._id) {
                navigate.toChat(creatorInfo._id);
            } else {
                navigate.toMessages();
            }
        } else if (loggedIn && !subscribed) {
            if (creatorRole === ROLE.CREATOR_CONTENT) {
                showFollowRequired("message or call this creator").then((confirmed) => {
                    if (confirmed) {
                        followBtn();
                    }
                });
            }
            if (creatorRole === ROLE.CREATOR_SUB) {
                showSubscriptionRequired("message or call this creator").then((confirmed) => {
                    if (confirmed) {
                        setSubscribeModalOpenSignal((n) => n + 1);
                    }
                });
            }
        } else {
            showSignInRequired("message or call this creator.").then((confirmed) => {
                if (confirmed) {
                    navigate.toLogin();
                }
            });
        }
    };

    // Follow button handler
    const followBtn = () => {
        // setPpvFollowModalOpen(true);
        if (loggedIn) {
            followBtnHandler();
        } else {
            showSignInRequired("follow").then((confirmed) => {
                if (confirmed) {
                    navigate.toLogin();
                }
            });
        }
    };

    // Unfollow button handler
    const unFollowBtn = () => {
        if (loggedIn) {
            unFollow();
        } else {
            showSignInRequired("follow").then((confirmed) => {
                if (confirmed) {
                    navigate.toLogin();
                }
            });
        }
    };

    // Toggle PPV follow modal
    // const togglePPVFollowModal = () => {
    //     setPpvFollowModalOpen(!ppvFollowModalOpen);
    // };

    // Handle follow
    const followBtnHandler = async () => {
        if (!creatorInfo?._id) {
            return;
        }

        setFollowLoading(true);
        try {
            const response = await followPPV(creatorInfo?._id);
            if (response?.success) {
                navigate.reload();
            } else {
                showError('Error', response?.error || 'Something went wrong. Please try again.');
            }
        } catch (error) {
            showError('Error', 'Something went wrong. Please try again.');
        } finally {
            setFollowLoading(false);
        }
    };

    // Unfollow handler
    const unFollow = async () => {
        const confirmed = await confirmUnfollow(
            `@${creatorInfo?.username}`,
            { imageUrl: creatorInfo?.profile_picture }
        );

        if (confirmed) {
            setFollowLoading(true);
            try {
                const result = await unfollowPPV(creatorInfo?._id);
                if (result.success) {
                    // showUnfollowSuccess(creatorInfo?.username).then(reloadPage);
                    setFollowLoading(false);
                    setPpvFollowModalOpen(false);
                    navigate.reload();
                } else {
                    setFollowLoading(false);
                    showError("Error", result.error || "Something went wrong. Please try again.");
                }
            } catch (error) {
                setFollowLoading(false);
                showError("Error", "Something went wrong. Please try again.");
            }
        }
    };

    // Fetch promotion data
    const fetchPromotionData = useCallback(async () => {
        if (!creatorInfo?._id) return;

        try {
            const result = await checkPromotion(creatorInfo._id);
            if (result.success && result.data) {
                if (result.data.eligible && result?.data?.promo) {
                    setPromotionData(result.data.promo);
                } else {
                    setPromotionData(null);
                }
            }
        } catch (error) {
            reportError(error, 'ProfileActions fetch promotion');
        }
    }, [creatorInfo?._id]);

    useEffect(() => {
        // Fetch promotion data only for subscription creators
        if (creatorInfo?.rol === ROLE.CREATOR_SUB) {
            if (loggedIn) {
                fetchPromotionData();
            } else {
                if (creatorInfo?.active_promotion) {
                    const getPromotionData = { ...creatorInfo?.active_promotion, active: true };
                    setPromotionData(getPromotionData);
                }
            }
        }
    }, [creatorInfo?.rol, fetchPromotionData]);

    const promotionText = useMemo(() => getDiscountOfferText(promotionData), [promotionData]);
    const promotionRemainingText = useMemo(() => getPromotionRemainingText(promotionData), [promotionData]);

    const isUserHaveFreeTrialSubscription = loggedIn ? isFreeTrialSubscriptionFunc(creatorInfo) : false;
    const getRemainingDays = () => calculateDaysRemaining(creatorInfo?.subscription?.["expire_at"]);
    const subscriptionState = creatorInfo?.subscription?.["state"] || null;

    const isSubscribed = loggedIn && subscribed &&
        Object.keys(creatorInfo?.subscription ?? {}).length > 0 &&
        (subscriptionState === PURCHASE_STATE.ACTIVE ||
            (subscriptionState === PURCHASE_STATE.EXPIRED && getRemainingDays() >= 0));

    const getSubscribeButtonText = useMemo(() => {
        if (!loggedIn) {
            return promotionText && promotionRemainingText?.isValid ? "Redeem" : "Subscribe";
        }

        if (!isUserHaveFreeTrialSubscription && !isSubscribed) {
            return promotionText && promotionRemainingText?.isValid ? "Redeem" : "Subscribe";
        }
        if (isUserHaveFreeTrialSubscription || (isSubscribed && subscriptionState === PURCHASE_STATE.ACTIVE)) {
            return "Subscribe";
        }
        return "Renew";
    }, [loggedIn, isUserHaveFreeTrialSubscription, isSubscribed, subscriptionState, promotionText, promotionRemainingText]);

    const isOfferActiveOfUser = useMemo(() => {
        if (isUserHaveFreeTrialSubscription) {
            return true;
        }
        if (creatorInfo?.subscription?.applied_promotion && Object.keys(creatorInfo?.subscription?.applied_promotion ?? {}).length > 0) {
            return true;
        }
        return false;
    }, [creatorInfo]);

    const initialPrice = useMemo(
        () => (creatorInfo?.subscription?.payment?.paymentForm ? getFormFieldValue(creatorInfo?.subscription?.payment?.paymentForm, 'initialPrice') : undefined),
        [creatorInfo?.subscription?.payment?.paymentForm]
    );

    return (
        <div className="d-flex flex-column align-items-end">
            {!isSubscribed && !isUserHaveFreeTrialSubscription && creatorRole === ROLE.CREATOR_SUB &&
                promotionText && promotionRemainingText && promotionRemainingText.isValid && (
                    <div className="profile-type-section mb-2">
                        <span className="profile-type-text">
                            {promotionText}{' '}
                            {promotionRemainingText.text && '•'}{' '}
                            {promotionRemainingText.text}
                        </span>
                    </div>
                )}

            {subscribed &&
                isUserHaveFreeTrialSubscription &&
                Object.keys(creatorInfo?.subscription ?? {}).length > 0 &&
                [PURCHASE_STATE.ACTIVE, PURCHASE_STATE.EXPIRED].includes(creatorInfo?.subscription["state"]) &&
                creatorRole === ROLE.CREATOR_SUB && (
                    <div className="profile-type-section mb-2">
                        <span className="profile-type-text-green">
                            Free Trial{' '}
                            {getRemainingDays() >= 0 ? '•' : ''}{' '}
                            {getSubscriptionText(
                                creatorInfo?.subscription?.["expire_at"]
                            )}
                        </span>
                    </div>
                )}

            {subscribed &&
                !isUserHaveFreeTrialSubscription &&
                creatorInfo?.subscription?.applied_promotion && Object.keys(creatorInfo?.subscription?.applied_promotion ?? {}).length > 0 &&
                calculateDaysRemaining(creatorInfo?.subscription?.["expire_at"]) >= 0 &&
                Object.keys(creatorInfo?.subscription ?? {}).length > 0 &&
                [PURCHASE_STATE.ACTIVE, PURCHASE_STATE.EXPIRED].includes(creatorInfo?.subscription["state"]) &&
                creatorRole === ROLE.CREATOR_SUB && (
                    <div className="profile-type-section mb-2">
                        <span className="profile-type-text-green">
                            {creatorInfo?.subscription?.state === PURCHASE_STATE.ACTIVE ?
                                <>
                                    Discount Access{' '}
                                    {getRemainingDays() >= 0 ? '•' : ''}{' '}
                                    {getSubscriptionText(
                                        creatorInfo?.subscription?.["expire_at"]
                                    )}
                                </>
                                :
                                null
                            }{' '}
                        </span>
                    </div>
                )}

            <div className="button-msg">
                <button className="jh-modern-profile-message-btn" onClick={messageBtn}>
                    <i className="fa-regular fa-comment"></i>
                </button>

                <div className="jh-modern-profile-subscribe-container">
                    <div>
                        {creatorInfo?.old_account !== true ? (
                            <>
                                {subscribed &&
                                    creatorInfo?.subscription?.["state"] === PURCHASE_STATE.ACTIVE &&
                                    creatorRole === ROLE.CREATOR_SUB &&
                                    !subscriptionLoading &&
                                    isEmailConfirmed
                                    ? (
                                        <UnsubscribeModal
                                            userInfo={creatorInfo}
                                            handleCallback={onSubscriptionSuccess}
                                        />
                                    ) : null}

                                {subscribed &&
                                    creatorInfo?.subscription?.["state"] === 3 &&
                                    creatorRole === ROLE.CREATOR_SUB &&
                                    !subscriptionLoading &&
                                    isEmailConfirmed ? (
                                    <UnsubscriptionModalInProgress
                                        userInfo={creatorInfo}
                                        handleCallback={onSubscriptionSuccess}
                                    />
                                ) : null}

                                {!subscriptionLoading &&
                                    creatorRole === ROLE.CREATOR_SUB &&
                                    Object.keys(creatorInfo?.subscription ?? {}).length > 0 &&
                                    creatorInfo?.subscription["state"] === PURCHASE_STATE.PENDING ? (
                                    <SubscribeModalWaitingPayment
                                        userInfo={creatorInfo}
                                        handleCallback={onSubscriptionSuccess}
                                    />
                                ) : null}


                                {((!loggedIn && creatorRole === ROLE.CREATOR_SUB) ||
                                    (loggedIn &&
                                        !subscriptionLoading &&
                                        creatorRole === ROLE.CREATOR_SUB &&
                                        ((!isUserHaveFreeTrialSubscription && !isSubscribed) ||
                                            (isUserHaveFreeTrialSubscription || isSubscribed)
                                        ) &&
                                        (Object.keys(creatorInfo?.subscription ?? {}).length <= 0 ||
                                            [PURCHASE_STATE.INITIAL, PURCHASE_STATE.EXPIRED].includes(creatorInfo?.subscription["state"]))
                                    )
                                ) &&
                                    (
                                        <SubscribeModalLoggedIn
                                            creatorInfo={creatorInfo}
                                            btnText={getSubscribeButtonText}
                                            isButtonClickable={isUserHaveFreeTrialSubscription ? false : true}
                                            isFreeTrialSubscription={promotionData?.offer_type === OFFER_TYPE.FREE_TRIAL ? true : false}
                                            isSubscribed={isSubscribed}
                                            isOfferActiveOfUser={isOfferActiveOfUser}
                                            isRenew={false}
                                            openSignal={subscribeModalOpenSignal}
                                            handleCallback={onSubscriptionSuccess}
                                        />
                                    )}

                                {creatorInfo &&
                                    creatorInfo.rol === ROLE.CREATOR_CONTENT &&
                                    !subscribed && (
                                        <button
                                            onClick={followBtn}
                                            className="jh-modern-profile-follow-btn"
                                            disabled={followLoading}
                                        >
                                            {followLoading && (
                                                <span
                                                    className="spinner-border spinner-border-sm btn-loading-spinner mr-1"
                                                    role="status"
                                                    aria-hidden="true"
                                                > </span>
                                            )}
                                            Follow
                                        </button>
                                    )}

                                {creatorInfo &&
                                    creatorInfo.rol === ROLE.CREATOR_CONTENT &&
                                    subscribed && (
                                        <button
                                            onClick={unFollowBtn}
                                            className="jh-modern-profile-following-btn"
                                            disabled={followLoading}
                                        >
                                            {followLoading && (
                                                <span
                                                    className="spinner-border spinner-border-sm btn-loading-spinner mr-1"
                                                    role="status"
                                                    aria-hidden="true"
                                                > </span>
                                            )}
                                            Following
                                        </button>
                                    )}
                            </>
                        ) : null}
                    </div>
                </div>
            </div>

            {/* Show price if user is not subscribed and has no active promotion and is active subscription*/}
            <div className="text-center">
                {creatorRole === ROLE.CREATOR_SUB && (
                    !isUserHaveFreeTrialSubscription && !isSubscribed && promotionText && promotionRemainingText && promotionRemainingText.isValid ?
                        <div className="profile-price-section">
                            <span className="profile-price-text">
                                {getPrice.isFreeTrial ? (
                                    <>
                                        <span className="profile-price-text-free-trial">
                                            {getPrice.displayText}{' '}
                                        </span>
                                        <span className="profile-price-text-real">
                                            ${getAmountWithExtraCharge(getPrice.currentPrice, vatPercentage)}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        {getPrice.currentPrice > 0 && getPrice.originalPrice > 0 ? (
                                            <>
                                                <span className="profile-price">
                                                    ${getAmountWithExtraCharge(getPrice.currentPrice, vatPercentage)}
                                                </span>
                                                <span className="profile-price-text-real">
                                                    (${getAmountWithExtraCharge(getPrice.originalPrice, vatPercentage)}/mo)
                                                </span>
                                            </>
                                        ) : (
                                            <span className="profile-price">
                                                ${getAmountWithExtraCharge(getPrice.currentPrice, vatPercentage)}/mo
                                            </span>
                                        )}
                                    </>
                                )}
                            </span>
                        </div>
                        :
                        (!isSubscribed || subscriptionState === PURCHASE_STATE.INITIAL || subscriptionState === PURCHASE_STATE.EXPIRED) && (
                            <div className="profile-price-section">
                                {!isUserHaveFreeTrialSubscription && subscriptionState === PURCHASE_STATE.EXPIRED ?
                                    <span className="profile-price-text">
                                        ${getAmountWithExtraCharge(price, vatPercentage)}/mo
                                        {' '}
                                        {getRemainingDays() >= 0 ? '•' : ''}{' '}
                                        {getEndSubscriptionText(creatorInfo?.subscription?.["expire_at"])}
                                    </span>
                                    :
                                    <span className="profile-price-text w-100">
                                        ${getAmountWithExtraCharge(price, vatPercentage)}/mo
                                    </span>
                                }
                            </div>
                        )
                )}

                {/* Show price + renews for active subscription (including claimed discount / free trial) */}
                {subscribed &&
                    !isUserHaveFreeTrialSubscription &&
                    [PURCHASE_STATE.ACTIVE].includes(creatorInfo?.subscription["state"]) &&
                    creatorRole === ROLE.CREATOR_SUB && (
                        <div className="profile-type-section mb-2">
                            <span className="profile-price-text">
                                {initialPrice === 0 || initialPrice === undefined
                                    ? `$${getAmountWithExtraCharge(price, vatPercentage)}/mo`
                                    : `$${initialPrice}/mo`
                                }{' '}
                                •
                                Renews {formatDateTimeZone(creatorInfo?.subscription?.["expire_at"], 'Mon DD')}
                            </span>
                        </div>
                    )}
            </div>




            {/* <PPVFollowModal
                modalOpen={ppvFollowModalOpen}
                toggleModal={togglePPVFollowModal}
                creatorInfo={creatorInfo}
            /> */}

        </div>
    );
};

export default ProfileActions;
